/**
 * Checks if a string contains only Latin script characters
 * @param text The text to check
 * @returns boolean - true if the text is in Latin script
 */
export const isLatinScript = (text: string): boolean => {
  if (!text) return false;
  return /^[\p{Script=Latin}\s'-]+$/u.test(text);
};

/**
 * Fixes double-encoded Korean/Unicode text
 */
const fixDoubleEncoded = (str: string): string => {
  try {
    // First, try to decode as if it was double-encoded
    const decoded = decodeURIComponent(escape(str));
    // If we get here, it was double-encoded
    return decoded;
  } catch (e) {
    // If it wasn't double-encoded, return as is
    return str;
  }
};

/**
 * Safely decodes and normalizes a string with proper Unicode handling
 */
export const safeDecode = (str: string | undefined | null): string => {
  if (!str) return "";

  try {
    // First try to decode URI components if it's encoded
    // const decoded = decodeURIComponent(str);

    // First fix any double-encoded text
    const fixed = fixDoubleEncoded(str);
    // Then normalize to handle combining characters
    return fixed.normalize("NFC");
  } catch (e) {
    // If decoding fails, just normalize the original string
    return String(str).normalize("NFC");
  }
};
/**
 * Normalizes and formats a name, preserving non-Latin characters
 * @param name The name to format
 * @returns string - The formatted name with preserved non-Latin characters
 */
export const formatName = (name: string | undefined | null): string => {
  if (!name) return "";

  // Normalize the string to handle combined characters
  const normalized = name.normalize("NFC");

  // If it's Latin script, apply the special formatting
  if (isLatinScript(normalized)) {
    return normalized
      .toLowerCase()
      .split(" ")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ")
      .replace(/\b(Mc|Mac|O'|D'|L'|De La |De |Van )([a-z])/g, (_, p1, p2) => p1 + p2.toUpperCase());
  }

  // For non-Latin scripts, return the normalized string as-is
  // return normalized;
  return safeDecode(name);
};
/**
 * Formats a name input in real-time as the user types
 * @param value The current input value
 * @param previousValue The previous input value
 * @returns string - The formatted name
 */
export const formatNameInput = (value: string, previousValue: string = ""): string => {
  // If the user is deleting characters, don't modify the input
  if (value.length < previousValue.length) {
    return value;
  }

  // Don't format empty strings or single characters
  if (!value || value.length <= 1) {
    return value;
  }

  // Only format if the last character entered is a letter or space
  const lastChar = value.slice(-1);
  if (!/[a-zA-Z\s]/.test(lastChar)) {
    return value;
  }

  return formatName(value);
};

export type PersonNameLike = {
  name?: string | null;
  full_name?: string | null;
  display_name?: string | null;
  given_name?: string | null;
  family_name?: string | null;
  first_name?: string | null;
  last_name?: string | null;
  givenName?: string | null;
  familyName?: string | null;
  name_order?: "as_entered" | "given_family" | "family_given" | string | null;
};

const trimmed = (value: unknown): string => (typeof value === "string" ? value.trim() : "");

const isKoreanLocale = (locale?: string): boolean => locale?.toLowerCase().startsWith("ko") ?? false;

export const formatStructuredName = (person: PersonNameLike, locale?: string): string => {
  const displayName = trimmed(person.display_name);
  if (displayName) return formatName(displayName);

  const givenName = trimmed(person.given_name || person.first_name || person.givenName);
  const familyName = trimmed(person.family_name || person.last_name || person.familyName);
  if (!givenName && !familyName) return formatName(person.name || person.full_name);

  const order = person.name_order || (isKoreanLocale(locale) ? "family_given" : "given_family");
  if (order === "as_entered") {
    return formatName(person.name || person.full_name) || [givenName, familyName].filter(Boolean).join(" ");
  }

  const orderedParts = order === "family_given" ? [familyName, givenName] : [givenName, familyName];
  const separator = isKoreanLocale(locale) ? "" : " ";
  return orderedParts.filter(Boolean).join(separator);
};

export const getPersonDisplayName = (person: PersonNameLike | string | null | undefined, locale?: string): string => {
  if (typeof person === "string") return formatName(person);
  if (!person) return "";
  return formatStructuredName(person, locale);
};

export const getPersonInitials = (person: PersonNameLike | string | null | undefined, locale?: string): string => {
  const displayName = getPersonDisplayName(person, locale);
  if (!displayName) return "";
  if (/^[\uAC00-\uD7A3]+$/.test(displayName)) return displayName.slice(0, 1);
  return displayName
    .split(/\s+/)
    .map((part) => part.charAt(0).toUpperCase())
    .filter(Boolean)
    .join("")
    .slice(0, 2);
};

export const comparePersonNames = (
  left: PersonNameLike | string | null | undefined,
  right: PersonNameLike | string | null | undefined,
  locale?: string
): number =>
  new Intl.Collator(isKoreanLocale(locale) ? "ko-KR" : locale || "en-US", {
    sensitivity: "base",
    numeric: true
  }).compare(getPersonDisplayName(left, locale), getPersonDisplayName(right, locale));
